import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  Download, Share2, ExternalLink, Copy, Trash2, Tag, 
  X, Play, Image as ImageIcon, Video, Music, Eye,
  MoreVertical, RefreshCw, AlertCircle, Check, Search,
  Filter, ChevronDown, Loader2, Calendar, Sparkles, Upload
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * MediaGallery - Visual-first gallery component for Delphi AI
 * 
 * Features:
 * - Responsive grid layout (2-6 columns)
 * - Thumbnail display with fallbacks
 * - Preview modal with full media view
 * - Download support (desktop + mobile)
 * - Share functionality (Web Share API + clipboard)
 * - Open in new tab
 * - Copy prompt
 * - Delete with confirmation
 */

const MediaGallery = ({ 
  items = [], 
  token, 
  onRefresh, 
  onDelete,
  contentType = 'all', // 'all', 'image', 'video', 'audio'
  showFilters = true,
  emptyMessage = "No media yet",
  emptyAction = null
}) => {
  // State
  const [filteredItems, setFilteredItems] = useState(items);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [previewItem, setPreviewItem] = useState(null);
  const [downloading, setDownloading] = useState({});
  const [deleting, setDeleting] = useState({});

  // Filter and sort items when dependencies change
  useEffect(() => {
    let result = [...items];

    // Type filter
    if (typeFilter !== 'all') {
      result = result.filter(item => item.type === typeFilter);
    }

    // Source filter
    if (sourceFilter !== 'all') {
      result = result.filter(item => item.source === sourceFilter);
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item => 
        (item.title?.toLowerCase().includes(query)) ||
        (item.prompt?.toLowerCase().includes(query))
      );
    }

    // Sort
    result.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    // Filter out failed items by default (unless specifically searching)
    if (!searchQuery.trim()) {
      result = result.filter(item => item.status !== 'failed');
    }

    setFilteredItems(result);
  }, [items, typeFilter, sourceFilter, sortOrder, searchQuery]);

  // Get thumbnail URL with fallbacks
  const getThumbnailUrl = (item) => {
    // Priority: thumbnail_url > image_url > video_url (for video frames) > null
    if (item.thumbnail_url) return item.thumbnail_url;
    if (item.image_url) return item.image_url;
    if (item.type === 'video' && item.video_url) return null; // Will show play icon
    return null;
  };

  // Get media URL for preview/download
  const getMediaUrl = (item) => {
    if (item.type === 'image') return item.image_url || item.thumbnail_url;
    if (item.type === 'video') return item.video_url;
    if (item.type === 'audio') return item.audio_url;
    return item.image_url || item.video_url || item.audio_url;
  };

  // Download handler - works on both desktop and mobile
  const handleDownload = async (item, e) => {
    e?.stopPropagation();
    
    const itemId = item.id;
    setDownloading(prev => ({ ...prev, [itemId]: true }));
    toast.info('Download started...');

    try {
      // Determine the correct download endpoint based on type
      let downloadUrl;
      if (item.type === 'image') {
        downloadUrl = `${API}/images/${itemId}/download`;
      } else if (item.type === 'video') {
        downloadUrl = `${API}/reels/${itemId}/download`;
      } else if (item.type === 'audio') {
        downloadUrl = `${API}/music/${itemId}/download`;
      } else {
        // Fallback to direct URL download
        downloadUrl = getMediaUrl(item);
      }

      // For API endpoints, use axios with blob response
      if (downloadUrl.includes('/api/')) {
        const response = await axios.get(downloadUrl, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });

        // Get filename from Content-Disposition header or generate one
        const contentDisposition = response.headers['content-disposition'];
        let filename = `delphi_${item.type}_${itemId}`;
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        } else {
          // Add extension based on type
          const extensions = { image: '.png', video: '.mp4', audio: '.mp3' };
          filename += extensions[item.type] || '.bin';
        }

        // Create blob URL and trigger download
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const blobUrl = window.URL.createObjectURL(blob);

        // Check if we're on mobile and Web Share is available for files
        if (navigator.share && navigator.canShare && navigator.canShare({ files: [new File([blob], filename)] })) {
          try {
            await navigator.share({
              files: [new File([blob], filename, { type: blob.type })],
              title: item.title || 'Delphi AI Media'
            });
            toast.success('Shared successfully!');
          } catch (shareError) {
            // User cancelled or share failed, fall back to download
            triggerBrowserDownload(blobUrl, filename);
          }
        } else {
          triggerBrowserDownload(blobUrl, filename);
        }

        // Cleanup
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);

      } else {
        // Direct URL download (R2 public URLs)
        window.open(downloadUrl, '_blank');
        toast.success('Download started');
      }

    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download failed — try again');
    } finally {
      setDownloading(prev => ({ ...prev, [itemId]: false }));
    }
  };

  // Helper to trigger browser download
  const triggerBrowserDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Download complete!');
  };

  // Share handler - Web Share API with clipboard fallback
  const handleShare = async (item, e) => {
    e?.stopPropagation();
    
    const mediaUrl = getMediaUrl(item);
    const shareData = {
      title: item.title || 'Delphi AI Creation',
      text: item.prompt || 'Check out this content from Delphi AI',
      url: mediaUrl
    };

    try {
      if (navigator.share && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success('Shared successfully!');
      } else {
        // Fallback: copy URL to clipboard
        await navigator.clipboard.writeText(mediaUrl);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        // Try clipboard as final fallback
        try {
          await navigator.clipboard.writeText(mediaUrl);
          toast.success('Link copied to clipboard!');
        } catch {
          toast.error('Failed to share');
        }
      }
    }
  };

  // Open in new tab
  const handleOpenInNewTab = (item, e) => {
    e?.stopPropagation();
    const url = getMediaUrl(item);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('No media URL available');
    }
  };

  // Copy prompt
  const handleCopyPrompt = async (item, e) => {
    e?.stopPropagation();
    if (item.prompt) {
      try {
        await navigator.clipboard.writeText(item.prompt);
        toast.success('Prompt copied!');
      } catch {
        toast.error('Failed to copy prompt');
      }
    } else {
      toast.info('No prompt available');
    }
  };

  // Delete handler
  const handleDelete = async (item, e) => {
    e?.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    setDeleting(prev => ({ ...prev, [item.id]: true }));

    try {
      if (onDelete) {
        await onDelete(item);
      } else {
        // Default delete API call
        let endpoint;
        if (item.type === 'image') endpoint = `${API}/images/${item.id}`;
        else if (item.type === 'video') endpoint = `${API}/reels/${item.id}`;
        else if (item.type === 'audio') endpoint = `${API}/music/${item.id}`;

        if (endpoint) {
          await axios.delete(endpoint, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      }
      
      toast.success('Deleted successfully');
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete');
    } finally {
      setDeleting(prev => ({ ...prev, [item.id]: false }));
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  // Get type icon
  const getTypeIcon = (type) => {
    switch (type) {
      case 'image': return <ImageIcon className="w-3 h-3" />;
      case 'video': return <Video className="w-3 h-3" />;
      case 'audio': return <Music className="w-3 h-3" />;
      default: return <ImageIcon className="w-3 h-3" />;
    }
  };

  // Render thumbnail
  const renderThumbnail = (item) => {
    const thumbnailUrl = getThumbnailUrl(item);
    const isLoading = item.status === 'generating' || item.status === 'processing';
    const isFailed = item.status === 'failed';

    if (isLoading) {
      return (
        <div className="w-full aspect-square bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-lg flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      );
    }

    if (isFailed) {
      return (
        <div className="w-full aspect-square bg-gradient-to-br from-red-950/30 to-zinc-900 rounded-lg flex flex-col items-center justify-center gap-2">
          <AlertCircle className="w-8 h-8 text-red-400" />
          <span className="text-xs text-red-400">Failed</span>
        </div>
      );
    }

    if (thumbnailUrl) {
      return (
        <div className="relative w-full aspect-square">
          <img
            src={thumbnailUrl}
            alt={item.title || item.prompt || 'Media'}
            className="w-full h-full object-cover rounded-lg"
            loading="lazy"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden w-full h-full absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-lg items-center justify-center">
            <AlertCircle className="w-8 h-8 text-zinc-600" />
          </div>
          {item.type === 'video' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 bg-black/60 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Play className="w-6 h-6 text-white ml-1" fill="white" />
              </div>
            </div>
          )}
        </div>
      );
    }

    // Fallback placeholder
    return (
      <div className="w-full aspect-square bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-lg flex items-center justify-center">
        {item.type === 'video' ? (
          <div className="w-12 h-12 bg-black/40 rounded-full flex items-center justify-center">
            <Play className="w-6 h-6 text-zinc-400 ml-1" />
          </div>
        ) : item.type === 'audio' ? (
          <Music className="w-10 h-10 text-zinc-600" />
        ) : (
          <ImageIcon className="w-10 h-10 text-zinc-600" />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      {showFilters && (
        <div className="flex flex-wrap items-center gap-3 pb-4 border-b border-zinc-800">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-zinc-900 border-zinc-800"
            />
          </div>

          {/* Type Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Filter className="w-4 h-4" />
                {typeFilter === 'all' ? 'All Types' : typeFilter.charAt(0).toUpperCase() + typeFilter.slice(1)}
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => setTypeFilter('all')}>All Types</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTypeFilter('image')}>Images</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTypeFilter('video')}>Videos</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTypeFilter('audio')}>Audio</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Source Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                {sourceFilter === 'all' ? 'All Sources' : sourceFilter === 'generated' ? 'Generated' : 'Uploaded'}
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => setSourceFilter('all')}>All Sources</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSourceFilter('generated')}>Generated</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSourceFilter('upload')}>Uploaded</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Sort */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Calendar className="w-4 h-4" />
                {sortOrder === 'newest' ? 'Newest' : 'Oldest'}
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => setSortOrder('newest')}>Newest First</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortOrder('oldest')}>Oldest First</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Refresh */}
          {onRefresh && (
            <Button variant="ghost" size="sm" onClick={onRefresh}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}

      {/* Empty State */}
      {filteredItems.length === 0 && (
        <div className="py-16 text-center">
          <div className="w-20 h-20 mx-auto mb-4 bg-zinc-800/50 rounded-2xl flex items-center justify-center">
            <ImageIcon className="w-10 h-10 text-zinc-600" />
          </div>
          <p className="text-lg font-medium text-zinc-300 mb-2">{emptyMessage}</p>
          <p className="text-sm text-zinc-500 mb-4">
            {searchQuery ? 'Try a different search term' : 'Generate or upload content to see it here'}
          </p>
          {emptyAction}
        </div>
      )}

      {/* Gallery Grid */}
      {filteredItems.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className="group relative bg-zinc-900/50 rounded-xl overflow-hidden cursor-pointer transition-all hover:ring-2 hover:ring-orange-500/50 hover:scale-[1.02]"
              onClick={() => setPreviewItem(item)}
              data-testid={`gallery-item-${item.id}`}
            >
              {/* Thumbnail */}
              {renderThumbnail(item)}

              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                {/* Quick Actions */}
                <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 px-2 bg-black/50 hover:bg-black/70 text-white"
                    onClick={(e) => handleDownload(item, e)}
                    disabled={downloading[item.id]}
                  >
                    {downloading[item.id] ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                  </Button>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 bg-black/50 hover:bg-black/70 text-white">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setPreviewItem(item); }}>
                        <Eye className="w-4 h-4 mr-2" /> Preview
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => handleDownload(item, e)}>
                        <Download className="w-4 h-4 mr-2" /> Download
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => handleShare(item, e)}>
                        <Share2 className="w-4 h-4 mr-2" /> Share
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => handleOpenInNewTab(item, e)}>
                        <ExternalLink className="w-4 h-4 mr-2" /> Open in New Tab
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={(e) => handleCopyPrompt(item, e)}>
                        <Copy className="w-4 h-4 mr-2" /> Copy Prompt
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={(e) => handleDelete(item, e)}
                        className="text-red-400 focus:text-red-400"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Badges */}
              <div className="absolute top-2 left-2 flex gap-1">
                <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                  item.source === 'upload' 
                    ? 'bg-blue-500/80 text-white' 
                    : 'bg-orange-500/80 text-white'
                }`}>
                  {item.source === 'upload' ? 'Uploaded' : 'AI'}
                </span>
              </div>

              {/* Title bar */}
              <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/90 to-transparent">
                <p className="text-xs font-medium text-white truncate">
                  {item.title || item.prompt?.slice(0, 30) || 'Untitled'}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="flex items-center gap-1 text-[10px] text-zinc-400">
                    {getTypeIcon(item.type)}
                  </span>
                  <span className="text-[10px] text-zinc-500">
                    {formatDate(item.created_at)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      <Dialog open={!!previewItem} onOpenChange={() => setPreviewItem(null)}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] p-0 bg-zinc-950 border-zinc-800 overflow-hidden">
          {previewItem && (
            <>
              {/* Media Display */}
              <div className="relative bg-black flex items-center justify-center min-h-[300px] max-h-[60vh]">
                {previewItem.type === 'image' && (
                  <img
                    src={previewItem.image_url || previewItem.thumbnail_url}
                    alt={previewItem.title || previewItem.prompt}
                    className="max-w-full max-h-[60vh] object-contain"
                  />
                )}
                {previewItem.type === 'video' && (
                  <video
                    src={previewItem.video_url}
                    controls
                    autoPlay
                    className="max-w-full max-h-[60vh]"
                    poster={previewItem.thumbnail_url}
                  />
                )}
                {previewItem.type === 'audio' && (
                  <div className="p-8 flex flex-col items-center gap-4">
                    <div className="w-32 h-32 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center">
                      <Music className="w-16 h-16 text-white" />
                    </div>
                    <audio src={previewItem.audio_url} controls autoPlay className="w-full max-w-md" />
                  </div>
                )}

                {/* Close button */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2 h-8 w-8 p-0 bg-black/50 hover:bg-black/70 text-white rounded-full"
                  onClick={() => setPreviewItem(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Info Panel */}
              <div className="p-4 space-y-4 max-h-[30vh] overflow-y-auto">
                {/* Title & Date */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      {previewItem.title || 'Untitled'}
                    </h3>
                    <p className="text-sm text-zinc-500">
                      {new Date(previewItem.created_at).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      previewItem.source === 'upload'
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'bg-orange-500/20 text-orange-400'
                    }`}>
                      {previewItem.source === 'upload' ? 'Uploaded' : 'Generated'}
                    </span>
                    <span className="px-2 py-1 text-xs font-medium rounded bg-zinc-800 text-zinc-400 capitalize">
                      {previewItem.type}
                    </span>
                  </div>
                </div>

                {/* Prompt */}
                {previewItem.prompt && (
                  <div className="bg-zinc-900 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-zinc-500">PROMPT</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={(e) => handleCopyPrompt(previewItem, e)}
                      >
                        <Copy className="w-3 h-3 mr-1" /> Copy
                      </Button>
                    </div>
                    <p className="text-sm text-zinc-300">{previewItem.prompt}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-800">
                  <Button
                    onClick={(e) => handleDownload(previewItem, e)}
                    disabled={downloading[previewItem.id]}
                    className="flex-1 sm:flex-none"
                  >
                    {downloading[previewItem.id] ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Download
                  </Button>
                  <Button
                    variant="outline"
                    onClick={(e) => handleShare(previewItem, e)}
                    className="flex-1 sm:flex-none"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                  <Button
                    variant="outline"
                    onClick={(e) => handleOpenInNewTab(previewItem, e)}
                    className="flex-1 sm:flex-none"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={(e) => {
                      handleDelete(previewItem, e);
                      setPreviewItem(null);
                    }}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MediaGallery;
